import { GraphResolverBase } from './GraphResolverBase';
import { ComponentFactory, ViewContainerRef } from '@angular/core';
import { ConfigDataService } from '../services/config-data.service';
import { GraphDataService } from '../services/graph-data-service.service';
import { ViewPeriodService } from '../services/view-period.service';
import { Subscription, BehaviorSubject, combineLatest } from 'rxjs';
import { PopoverController } from '@ionic/angular';
import { DynamicDualNumberCardComponent } from '../shared-components/dynamic/dynamic-dual-numbercard/dynamic-dual-numbercard.component';
import { DynamicGraphAdditionalInput } from '../objects/config';
import { CustomTextTooltipsComponent } from '../pages/home/general/custom-text-tooltips/custom-text-tooltips.component';
import { PurchaseBagTooltipsComponent } from '../shared-components/general/purchase-bag-tooltips/purchase-bag-tooltips.component';

export class HeadcountATSNVHNumberCardResolver extends GraphResolverBase {

    public async createComponent(
        componentFactory: ComponentFactory<unknown>,
        _additionalInput: string | DynamicGraphAdditionalInput | undefined,
        configDataService: ConfigDataService,
        graphDataService: GraphDataService,
        popoverController: PopoverController,
        _viewPeriodService: ViewPeriodService,
        viewContainerRef: ViewContainerRef,
        subscription: Subscription
    ) {
        // load data here
        graphDataService.baseGraphData.addDependency(this.dataDependency);

        await configDataService.loadAppConfig();

        const componentRef = viewContainerRef.createComponent(componentFactory);
        const comInstance = componentRef.instance as DynamicDualNumberCardComponent;

        const headcountNumberData$ = new BehaviorSubject('0');
        const headcountLabel$ = new BehaviorSubject('0 % (0)');
        const headcountLabelIcon$ = new BehaviorSubject('trending-up');
        const headcountLabelStyle$ = new BehaviorSubject({ fontSize: '16px', fontWeight: '500', color: '#5BBF93' });

        subscription.add(combineLatest([configDataService.isEntranceDataMode$, graphDataService.currentHeadCountEntranceData$, graphDataService.currentHeadCountExitData$]).subscribe(
            ([isEntrance, headCountEntranceData, headCountExitData]) => {
                const headCountData = isEntrance ? headCountEntranceData : headCountExitData;
                headcountNumberData$.next(headCountData.headCount?.toLocaleString());
                headcountLabel$.next(`${headCountData.diffPercent?.toFixed(1)} % (${headCountData.diff?.toLocaleString()})`);
                headcountLabelIcon$.next(headCountData.diff > 0 ? 'trending-up' : 'trending-down');
                headcountLabelStyle$.next({ fontSize: '16px', fontWeight: '500', color: headCountData.diff >= 0 ? '#5BBF93' : '#E25144' });
            }
        ));

        const averageTimeSpentNumberData$ = new BehaviorSubject('0');
        const averageTimeSpentLabel$ = new BehaviorSubject('0 % (0)');
        const averageTimeSpentLabelIcon$ = new BehaviorSubject('trending-up');
        const averageTimeSpentLabelStyle$ = new BehaviorSubject({ fontSize: '16px', fontWeight: '500', color: '#5BBF93' });

        subscription.add(graphDataService.currentAverageTimeSpentData$.subscribe((averageTimeSpentData) => {
            averageTimeSpentNumberData$.next(`${(averageTimeSpentData.avgTimeSpent / 60).toFixed(1)}`);
            averageTimeSpentLabel$.next(`${averageTimeSpentData.diffPercent.toFixed(1)} % (${(averageTimeSpentData.diff / 60).toFixed(1)})`);
            averageTimeSpentLabelIcon$.next((averageTimeSpentData.diff / 60) > 0 ? 'trending-up' : 'trending-down');
            averageTimeSpentLabelStyle$.next({ fontSize: '16px', fontWeight: '500', color: (averageTimeSpentData.diff / 60) >= 0 ? '#5BBF93' : '#E25144' });
        }));

        const purchaseRateNumberData$ = new BehaviorSubject('0 %');
        const purchaseRateLabel$ = new BehaviorSubject('0 %');
        const purchaseRateLabelIcon$ = new BehaviorSubject('trending-up');
        const purchaseRateLabelStyle$ = new BehaviorSubject({ fontSize: '16px', fontWeight: '500', color: '#5BBF93' });

        subscription.add(graphDataService.currentpurchaseRateData$.subscribe((purchaseRateData) => {
            purchaseRateNumberData$.next(`${purchaseRateData.purchase.toFixed(1)} %`);
            purchaseRateLabel$.next(`${purchaseRateData.diff.toFixed(1)} %`);
            purchaseRateLabelIcon$.next(purchaseRateData.diff > 0 ? 'trending-up' : 'trending-down');
            purchaseRateLabelStyle$.next({ fontSize: '16px', fontWeight: '500', color: purchaseRateData.diff >= 0 ? '#5BBF93' : '#E25144' });
        }));

        const totalHeadcountCard = {
            isLock: this.isLock,
            title: configDataService.DISPLAY_LANGUAGE.HEADCONT,
            numberData$: headcountNumberData$,
            label$: headcountLabel$,
            labelIcon$: headcountLabelIcon$,
            labelStyle$: headcountLabelStyle$,
            sizeXS: '12',
            isFirst: true,
        };

        const averageTimeSpentCard = {
            isLock: this.isLock,
            title: configDataService.DISPLAY_LANGUAGE.DURATION_VISIT_NUMBER_CARD,
            numberData$: averageTimeSpentNumberData$,
            label$: averageTimeSpentLabel$,
            labelIcon$: averageTimeSpentLabelIcon$,
            labelStyle$: averageTimeSpentLabelStyle$,
            infoPopover: async (e: any) => {
                const avgTimeSpentPopover = await popoverController.create({
                    component: CustomTextTooltipsComponent,
                    componentProps: {
                        toolTipTitle: configDataService.DISPLAY_LANGUAGE.DURATION_VISIT_NUMBER_CARD,
                        toolTipDetails: ['Based on total entrance and exit timestamps.', 'Displayed in minutes.', 'Cannot be used during "LIVE" view.'],
                    },
                    cssClass: 'customer-segment-details-popover',
                    event: e,
                });
                return await avgTimeSpentPopover.present();
            },
            sizeXS: '6',
            isFirst: true,
        };

        const purchaseRateCard = {
            isLock: this.isLock,
            title: configDataService.DISPLAY_LANGUAGE.PURCHASE_RATE,
            numberData$: purchaseRateNumberData$,
            label$: purchaseRateLabel$,
            labelIcon$: purchaseRateLabelIcon$,
            labelStyle$: purchaseRateLabelStyle$,
            sizeXS: '6',
            infoPopover: async (e: any) => {
                const shoppingBagPopover = await popoverController.create({
                    component: PurchaseBagTooltipsComponent,
                    componentProps: {
                        toolTipTitle: configDataService.DISPLAY_LANGUAGE.PURCHASE_RATE,
                        toolTipDetails: ['Based on percentage of visitors carrying shopping bags.'],
                    },
                    cssClass: 'customer-segment-details-popover',
                    event: e,
                });
                return await shoppingBagPopover.present();
            }
        };

        comInstance.cards = [totalHeadcountCard, averageTimeSpentCard, purchaseRateCard];
        return comInstance;
    }

}
